import React from "react";
import { getClassName } from "@sscs/utils";
import { Menu, Select } from "../../index";
import styles from "./filterBar.module.scss";
const FIT_ESTIMATE = 160;
const GROW_MIN_WIDTH = 250;
const GAP = 16;
const SORT_WIDTH = 200;
const SORT_HOLD_OFF = 48;
const STACK_MIN = 500;
const STACK_MAX = 1400;
const filterWidth = (filter) => {
    if (filter.width !== undefined) {
        return parseInt(filter.width, 10);
    }
    if (filter.fit || filter.grow === 0) {
        return FIT_ESTIMATE;
    }
    return filter.minWidth !== undefined
        ? parseInt(filter.minWidth, 10)
        : GROW_MIN_WIDTH;
};
const snapThreshold = (raw) => Math.min(STACK_MAX, Math.max(STACK_MIN, Math.ceil(raw / 100) * 100));
const rowStackBelow = (row, sortWidth) => {
    const filtersWidth = row.reduce((sum, filter) => sum + filterWidth(filter), 0);
    const gaps = Math.max(row.length - 1, 0) * GAP;
    const sortSlot = sortWidth === false
        ? 0
        : (sortWidth !== undefined ? parseInt(sortWidth, 10) : SORT_WIDTH) +
            SORT_HOLD_OFF;
    return snapThreshold(filtersWidth + gaps + sortSlot);
};
const stretchingCount = (row) => row.filter((filter) => filter.fit !== true && filter.width === undefined && filter.grow !== 0).length;
const rowSingleColumnBelow = (row) => {
    const widest = Math.max(...row.map(filterWidth));
    return snapThreshold(2 * widest + GAP);
};
const rowThresholds = (row, sortWidth) => {
    const full = rowStackBelow(row, sortWidth);
    if (stretchingCount(row) <= 2) {
        return { stack: full };
    }
    return { pair: full, stack: rowSingleColumnBelow(row) };
};
const encodeSort = (sort) => `${sort.property}-${sort.reverse}`;
const decodeSort = (value) => {
    const separator = value.lastIndexOf("-");
    return {
        property: value.slice(0, separator),
        reverse: value.slice(separator + 1) === "true",
    };
};
const groupIntoRows = (filters) => {
    const rows = new Map();
    filters
        .filter((filter) => !filter.hidden)
        .forEach((filter) => {
        var _a, _b;
        const row = (_a = filter.row) !== null && _a !== void 0 ? _a : 1;
        rows.set(row, [...((_b = rows.get(row)) !== null && _b !== void 0 ? _b : []), filter]);
    });
    return [...rows.entries()]
        .sort(([left], [right]) => left - right)
        .map(([, row]) => row);
};
const Filter = ({ filter, }) => (React.createElement("div", { className: getClassName(styles.filter, {
        [styles.fixed]: filter.width !== undefined,
        [styles.fit]: filter.fit === true,
        [styles.snug]: filter.grow === 0 && !filter.fit,
    }), style: {
        "--filter-min-width": filter.minWidth,
        "--filter-width": filter.width,
        "--filter-grow": filter.grow,
    } }, filter.control));
const Sort = ({ id, options, value, onChange, label = "Sort by", width, }) => (React.createElement("div", { className: styles.sort, style: { "--sort-width": width } },
    React.createElement(Select, { displayEmpty: true, shrinkLabel: true, isFullWidth: true, id: id, labelId: `${id}-label`, label: label, value: encodeSort(value), onChange: (selected) => onChange(decodeSort(selected)) }, options.flatMap((option) => [
        React.createElement(Menu.Item, { key: `${option.property}-asc`, value: encodeSort({ property: option.property, reverse: false }) },
            "\u2191 ",
            option.label),
        React.createElement(Menu.Item, { key: `${option.property}-desc`, value: encodeSort({ property: option.property, reverse: true }) },
            "\u2193 ",
            option.label),
    ]))));
/**
 * The filter controls of a results page, laid out from a declarative list of {@link FilterDefinition}.
 * The bar owns the row rhythm, gaps, an optional sort control held clear at the right, and each row's
 * stacking threshold. Renders a filter band's contents, so it MUST live inside a `Page.Filters` or
 * `FiltersContainer` — those own the padding, divider, "N results" summary and container context.
 */
const FilterBar = ({ id, filters, sort, stacking = "auto", className, }) => {
    const rows = groupIntoRows(filters);
    const thresholds = (row, isLastRow) => stacking === "never"
        ? { stack: "never" }
        : rowThresholds(row, isLastRow && sort ? sort.width : false);
    const emptyRow = thresholds([], true);
    return (React.createElement("div", { id: id, className: getClassName(styles.root, className) },
        rows.length === 0 && sort ? (React.createElement("div", { className: styles.row, "data-pair-below": emptyRow.pair, "data-stack-below": emptyRow.stack },
            React.createElement(Sort, { ...sort }))) : null,
        rows.map((row, index) => {
            const rowStacking = thresholds(row, index === rows.length - 1);
            return (React.createElement("div", { key: row[0].id, className: styles.row, "data-pair-below": rowStacking.pair, "data-stack-below": rowStacking.stack },
                row.map((filter) => (React.createElement(Filter, { key: filter.id, filter: filter }))),
                sort && index === rows.length - 1 ? React.createElement(Sort, { ...sort }) : null));
        })));
};
export default FilterBar;
//# sourceMappingURL=FilterBar.js.map